/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import React, { useMemo } from 'react';
import styled from 'styled-components';

import { Flex, Grid, Spin, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { StackTable } from 'src/components/StackTable/StackTable';
import type {
  ILeaveApplicationFormData,
  IPartTimeJobEntityExtended
} from 'src/types/leaveApplications/types';
import { formatDate } from 'src/utils/date';

const { useBreakpoint } = Grid;
const { Title } = Typography;

const StyledTable = styled(Table)<any>`
  .ant-table {
    background: transparent;
  }

  .ant-table-cell {
    padding: 14px !important;
    background: transparent !important;
  }

  .ant-table-row > td {
    font-size: ${(props) => (props.screen.xl ? '16px' : '14px')};
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Section = styled.section`
  padding: 0 28px;
`;

const Text: React.FC<{ children: React.ReactNode; disabled?: boolean }> = ({
  children,
  disabled
}) => {
  const screen = useBreakpoint();
  const isMobile = !screen.xl;

  return (
    <Typography.Paragraph
      type={disabled ? 'secondary' : undefined}
      style={{ textAlign: isMobile ? 'right' : undefined }}
    >
      {children}
    </Typography.Paragraph>
  );
};

interface IProps {
  data: ILeaveApplicationFormData;
  partTimeJobsIsLoading: boolean;
}

type IPartTimeJobsTableEntity = IPartTimeJobEntityExtended & {
  key: number;
  usedDays: number;
  dateRange: string;
  vacationType: string;
};

export const PartTimeJobForm: React.FC<IProps> = ({ data, partTimeJobsIsLoading }) => {
  const screen = useBreakpoint();
  const isDesktop = !!screen.xl;

  const columns: ColumnsType<IPartTimeJobsTableEntity> = useMemo(
    () => [
      {
        title: 'Організація',
        key: 'organization',
        dataIndex: 'organization',
        render: (_, record) => <Text disabled={!record.isActive}>{record.company}</Text>
      },
      {
        title: 'Посада',
        key: 'position',
        dataIndex: 'position',
        render: (_, record) => <Text disabled={!record.isActive}>{record.position}</Text>
      },
      {
        title: 'Вид відпустки',
        key: 'type',
        dataIndex: 'type',
        render: (_, record) => <Text disabled={!record.isActive}>{record.vacationType}</Text>
      },
      {
        title: 'К-ть днів',
        key: 'usedDays',
        dataIndex: 'usedDays',
        width: 100,
        render: (_, record) => <Text disabled={!record.isActive}>{record.usedDays}</Text>
      },
      {
        title: 'Дати',
        key: 'dateRange',
        dataIndex: 'dateRange',
        width: 150,
        render: (_, record) => <Text disabled={!record.isActive}>{record.dateRange}</Text>
      },
      {
        title: 'Функц. керівник',
        key: 'functionalManager',
        dataIndex: 'functionalManager',
        width: 150,
        render: (_, record) => <Text disabled={!record.isActive}>{record.fnManager}</Text>
      },
      {
        title: 'Адмін. керівник',
        key: 'adminManager',
        dataIndex: 'adminManager',
        width: 150,
        render: (_, record) => <Text disabled={!record.isActive}>{record.admManager}</Text>
      }
    ],
    []
  );

  const dataSource: IPartTimeJobsTableEntity[] = useMemo(() => {
    const partTimeJobs = data.partTimeJobs?.items;

    return partTimeJobs.map((item, index) => ({
      ...item,
      key: index,
      usedDays: item.usedDays,
      dateRange: `${formatDate(item.dateFrom)} - ${formatDate(item.dateTo)}`,
      vacationType: item.vacationType
    }));
  }, [data.partTimeJobs]);

  function renderTable() {
    if (partTimeJobsIsLoading) {
      return (
        <Flex align="center" justify="center" flex={1} style={{ minHeight: 300 }}>
          <Spin size="large" />
        </Flex>
      );
    }

    return (
      <TableContainer>
        {isDesktop ? (
          <StyledTable
            columns={columns}
            dataSource={dataSource}
            screen={screen}
            pagination={false}
          />
        ) : (
          <StackTable columns={columns} dataSource={dataSource} />
        )}
      </TableContainer>
    );
  }

  return (
    <Section>
      <Title level={5} style={{ margin: 0, marginBottom: 24 }}>
        Сумісництво
      </Title>
      {renderTable()}
    </Section>
  );
};
